/**
 * A hand on the tonearm.
 *
 * The arm goes exactly where the finger puts it — no easing while it is held —
 * and the spot that was grabbed stays under the finger, wherever along the arm
 * that was. Let go over the record, the needle drops there and the music plays
 * from there; carried past the lead-in, it settles on its rest and the deck
 * stops.
 */

import { DIM } from '../geometry/dims.ts';
import {
  armYawForPoint,
  clampDragYaw,
  progressForArmYaw,
  shouldPark,
} from '../geometry/tonearm.ts';
import { angleDelta } from '../geometry/transport.ts';
import * as player from '../state/player.ts';
import { hardware } from './hardware.ts';
import { planeAt } from './picking.ts';
import { playFromArm, stopAtRest } from './playback.ts';

/**
 * The yaw the arm had over and above the finger's bearing when it was grabbed.
 * Holding it constant is what keeps the grabbed spot under the finger, instead
 * of the stylus snapping to the finger on the first move.
 */
let grabOffset = 0;

/** The yaw that aims the arm's own line at the pointer, on the arm's plane. */
function yawUnder(clientX: number, clientY: number): number | null {
  const p = planeAt(DIM.armY, clientX, clientY);
  return p ? armYawForPoint(p.x, p.z) : null;
}

/**
 * Take hold of the arm. Lifts the needle and stops the sound — otherwise the
 * transport keeps writing progress and fights the drag. Refuses while a record
 * is in flight, since there is no groove to cue.
 */
export function grabArm(clientX: number, clientY: number): boolean {
  const { tt } = hardware();
  if (!tt.state.seated) return false;
  const yaw = yawUnder(clientX, clientY);
  grabOffset = yaw === null ? 0 : angleDelta(tt.state.armYaw, yaw);
  tt.state.armFollow = false;
  if (player.deck.playing) player.deck.pause();
  void tt.setArmDown(false, 180);
  return true;
}

export function moveArm(clientX: number, clientY: number): void {
  const aimed = yawUnder(clientX, clientY);
  if (aimed === null) return;
  const { tt } = hardware();
  const { armYaw } = tt.state;
  /* The nearest turn of the finger's bearing, so it can cross the ±π seam. */
  const yaw = clampDragYaw(armYaw + angleDelta(aimed + grabOffset, armYaw));
  tt.dragArmTo(yaw);
  const progress = progressForArmYaw(yaw);
  tt.setProgress(progress);
  player.setPosition(progress * (player.deck.duration || 0));
}

/** Let go. The position the arm was carried to is committed either way. */
export function releaseArm(): void {
  const { tt } = hardware();
  player.deck.seekTo(tt.state.progress);
  if (shouldPark(tt.state.armYaw)) void stopAtRest();
  else void playFromArm();
}
